import * as Popover from '@radix-ui/react-popover';
import { useMemo, useState } from 'react';
import type { BrandAccess, TenantMe } from '@studio/contracts';
import { switchBrand } from '@/lib/active-brand';
import { Chevron, Icon, Tick } from './icons';

const ROLE_LABEL: Record<BrandAccess['role'], string> = { creator: 'creator', reviewer: 'read only', editor: 'editor', owner: 'owner' };

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');
}

/** The tenant block at the top of every sidebar. Click it, pick a brand, and the app reloads to that brand. */
export function BrandSwitcher({ me }: { me: TenantMe }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const active = me.active;
  const groups = useMemo(() => {
    const byOrg = new Map<string, { name: string; role: string; brands: BrandAccess[] }>();
    for (const b of me.brands) {
      if (q && !b.brandName.toLowerCase().includes(q.toLowerCase())) continue;
      const g = byOrg.get(b.orgId) ?? { name: b.orgName, role: b.role, brands: [] };
      g.brands.push(b);
      byOrg.set(b.orgId, g);
    }
    return [...byOrg.values()];
  }, [me.brands, q]);
  const index = active ? me.brands.findIndex((b) => b.brandId === active.brandId) + 1 : 0;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className="brand-block" aria-label="Switch brand">
          {active ? <BrandMark brand={active} className="mark" /> : <span className="mark">·</span>}
          <span className="grow">
            <span className="name">{active?.brandName ?? 'No brand'}</span>
            <span className="tenant">{active ? `${active.orgName} · brand ${index} of ${me.brands.length}` : 'No membership found'}</span>
          </span>
          <Chevron up={open} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="pop" align="start" sideOffset={6}>
          <label className="search">
            <Icon name="search" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a brand…" aria-label="Find a brand" />
          </label>
          {groups.map((g) => (
            <div key={g.name}>
              <div className="org">{g.name}<span>{ROLE_LABEL[g.brands[0]!.role]}</span></div>
              {g.brands.map((b) => {
                const on = active?.brandId === b.brandId;
                return (
                  <button key={b.brandId} type="button" className={`br${on ? ' on' : ''}`} onClick={() => (on ? setOpen(false) : switchBrand(b.brandSlug))}>
                    <BrandMark brand={b} />
                    <span className="nm">{b.brandName}</span>
                    <span className="rl">{ROLE_LABEL[b.role]}</span>
                    {on && <Tick />}
                  </button>
                );
              })}
            </div>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function BrandMark({ brand, className = 'mk' }: { brand: BrandAccess; className?: string }) {
  return <span className={`${className} brand-colour-${colourIndex(brand.brandId)}`}>{initials(brand.brandName)}</span>;
}

/** Eight stable swatches per brand, chosen by id so the same brand always gets the same mark. */
function colourIndex(id: string): number {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h % 8;
}
